import { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCheck, Loader2, Shield, Eye, Edit2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCESS_LEVEL_LABELS = {
    VIEWER: 'Lector',
    EDITOR: 'Editor'
};

const ACCESS_LEVEL_COLORS = {
    VIEWER: 'bg-gray-100 text-gray-700',
    EDITOR: 'bg-blue-100 text-blue-700'
};

const ACCESS_LEVEL_ICONS = {
    VIEWER: Eye,
    EDITOR: Edit2
};

export const ClientAuthorizedUsers = ({ clientId, token }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (clientId) {
            fetchAuthorizedUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    async function fetchAuthorizedUsers() {
        try {
            const res = await axios.get(`${API}/clients/${clientId}/access`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data);
        } catch (error) {
            console.error('Error fetching authorized users:', error);
            // Si el usuario no es admin, no puede ver esta sección
            if (error.response?.status === 403) {
                toast.error('Solo administradores pueden ver usuarios autorizados');
            } else {
                toast.error('Error al cargar usuarios autorizados');
            }
        } finally {
            setLoading(false);
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                    Usuarios Autorizados ({users.length})
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Usuarios con acceso a este cliente y sus marcas
                </p>
            </div>

            {/* Users List */}
            {users.length === 0 ? (
                <div className="p-12 text-center">
                    <UserCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No hay usuarios autorizados</p>
                    <p className="text-sm text-gray-400">
                        Los usuarios con acceso a este cliente aparecerán aquí
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Usuario
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nivel de Acceso
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha de Asignación
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {users.map(user => {
                                const AccessIcon = ACCESS_LEVEL_ICONS[user.access_level] || Shield;

                                return (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        {/* User */}
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium">
                                                    {user.user_nombre ? user.user_nombre.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {user.user_nombre}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Email */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-600">
                                                {user.user_email}
                                            </div>
                                        </td>

                                        {/* Access Level */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge className={`${ACCESS_LEVEL_COLORS[user.access_level]} border-0 text-xs flex items-center gap-1 w-fit`}>
                                                <AccessIcon className="h-3 w-3" />
                                                {ACCESS_LEVEL_LABELS[user.access_level]}
                                            </Badge>
                                        </td>

                                        {/* Created Date */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(user.created_at)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Info Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-start gap-2 text-sm text-gray-600">
                    <Shield className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-medium text-gray-700 mb-1">Niveles de acceso:</p>
                        <ul className="space-y-1 text-xs">
                            <li><strong>Lector:</strong> Puede ver el cliente y sus marcas</li>
                            <li><strong>Editor:</strong> Puede ver y editar el cliente y sus marcas</li>
                        </ul>
                        <p className="mt-2 text-xs text-gray-500">
                            Para gestionar accesos, usa el botón "Compartir" en la lista de clientes
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
